import { Component, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { Globals } from 'src/app/globals';
import { ModalService } from '@shared/services/modal.service';
import { Cf2KeyValuePair } from '@core/models/store.model';

@Component({
  selector: 'cf2-base',
  template: ``,
})
export class BaseComponent {
  protected _router = inject(Router);
  protected _ngZone = inject(NgZone);
  protected _globals = inject(Globals);
  protected _modalSvc = inject(ModalService);

  isReady = false;
  private _subscriptions: Subscription[] = [];

  navigate = (page: string, keys?: Partial<Cf2KeyValuePair>) => {
    if (!this._ngZone || !this._router) {
      console.warn('Injected services not ready in navigate()');
      return;
    }
    if (keys) this.setGlobalKeys(keys);
    this._ngZone.run(() => this._router.navigate([page], { state: { ...keys } }));
  };

  navigateBack = () => {
    const returnUrl = this._globals.returnUrl ?? 'home';
    this._ngZone.run(() => this._router.navigate([returnUrl]));
  };

  navigateClient = () => {
    this._ngZone.run(() => this._router.navigate(['clients']));
  };  

  navigateHome = () => {
    this._ngZone.run(() => this._router.navigate(['home']));
  };  

  generateUrl = (path: string, queryParams?: any) => {
    return this._router.serializeUrl(
      this._router.createUrlTree([path], { queryParams })
    );
  };

  openInNewTab = (path: string, queryParams?: any) => {
    const url = this.generateUrl(path, queryParams);
    window.open(url, '_blank');
  };

  clearStore = () => {
    this._globals.clearStore();
  };

  setGlobalKeys = (keys?: Partial<Cf2KeyValuePair>) => {
    if (!this._globals || !keys) return;
    Object.entries(keys).forEach(([key, value]) => {
      try {
        (this._globals as any)[key] = value;
      } catch (err) {
        console.error(`Failed to set globals.${key} = ${value}`, err);
      }
    });
  };

  timer$ = () => timer(60000);

  errorModal$ = () =>
    this._modalSvc.openDialog({ data: this._modalSvc.errorDialog }).afterClosed();

  get timeout() {
    return 60000;
  }

  get currentUrl() {
    return this._router.url;
  }

  get returnUrl() {
    return this._globals.returnUrl;
  }

  public minDate = new Date(new Date().getFullYear() - 4, new Date().getMonth(), new Date().getDate() - 1, 0, 0, 0, 0);
  public today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

  addSubscription = (sub: Subscription) => this._subscriptions.push(sub);
  addSubscriptions = (subs: Subscription[]) => subs.forEach((s) => this.addSubscription(s));
  unsubscribe = () => this._subscriptions.forEach((s) => s.unsubscribe());
}
